import { SprintAPI } from '../../utils/api';
import { dispatchTasksUpdatedEvent, removeTaskLinkFromParent } from '../campaignNesting/utils';
import { showToast } from '../../components/Toast';

/** Resolve parent task ID from the campaign or by scanning the parent sprint's tasks. */
export async function resolveParentTaskId(fullSprint, sprintId) {
  // The campaign might not have parentTaskId set (it's stored on the task, not the campaign)
  let parentTaskId = fullSprint.parentTaskId;
  if (fullSprint.parentSprintId && !parentTaskId) {
    try {
      const parentSprint = await SprintAPI.get(fullSprint.parentSprintId);
      if (parentSprint?.tasks) {
        const linkingTask = parentSprint.tasks.find((t) => t.nestedSprintId === sprintId);
        if (linkingTask) {
          parentTaskId = linkingTask.id;
        }
      }
    } catch (error) {
      console.warn('[TRASH] Could not fetch parent sprint to find task ID:', error);
    }
  }
  return parentTaskId;
}

/** Persist trash state on descendants. Local copies are metadata only — fetch full data first. */
export async function persistCascadeTrash({ cascadeDescendants, sprintId, deletedAt, expiresAt, setSprints }) {
  const cascadeFailures = [];
  for (const child of cascadeDescendants) {
    try {
      const fullChild = await SprintAPI.get(child.id);
      if (!fullChild) throw new Error('Sprint not found');
      const trashedChild = {
        ...fullChild,
        deletedAt,
        expiresAt,
        _trashedViaAncestorId: sprintId,
      };
      setSprints((prev) => prev.map((s) => (s.id === child.id ? trashedChild : s)));
      await SprintAPI.update(child.id, trashedChild);
    } catch (error) {
      cascadeFailures.push(child.name || child.id);
      console.error('[TRASH] Failed to cascade-trash nested campaign:', child.id, error);
    }
  }
  if (cascadeFailures.length > 0) {
    showToast(
      `Moved to trash, but ${cascadeFailures.length} nested campaign(s) could not be marked — they may reappear as orphans`,
      { type: 'error' }
    );
  }
}

/**
 * Nested campaign with preserveLinkedMission: keep the mission node, just unlink it.
 * The open parent view clears nestedSprintId itself (and persists via its normal
 * sync pipeline); we dispatch the event so any OTHER mounted view of the parent
 * also clears the stale link. We deliberately do NOT write the parent to the API
 * here to avoid racing the parent view's own debounced save.
 */
export function dispatchNestedSprintTrashed(parentSprintId, parentTaskId, sprintId) {
  if (!parentSprintId) return;
  window.dispatchEvent(new CustomEvent('nestedSprintTrashed', {
    detail: {
      parentSprintId,
      parentTaskId,
      nestedSprintId: sprintId,
    },
  }));
}

/** Remove the linking mission node from the parent (not just unlink). */
export async function removeLinkingMissionFromParent(parentSprintId, sprintId, setSprints) {
  if (!parentSprintId) return;
  try {
    const parentWithTaskRemoved = await removeTaskLinkFromParent(parentSprintId, sprintId);
    if (parentWithTaskRemoved) {
      await SprintAPI.update(parentSprintId, parentWithTaskRemoved);
      setSprints((prev) => prev.map((s) =>
        s.id === parentSprintId ? parentWithTaskRemoved : s
      ));
      dispatchTasksUpdatedEvent(
        parentSprintId,
        parentWithTaskRemoved.tasks,
        parentWithTaskRemoved.nodePositions
      );
      console.log('✅ Removed linking mission from parent campaign');
    }
  } catch (error) {
    console.error('Failed to remove linking mission from parent:', error);
  }
}
